import { trackTelemetryEvent } from "./telemetry";

export type UiInfraSurface = "home_social_load" | "admin_social_dashboard_refresh";
type UiInfraStatus = "ok" | "error";

type UiInfraDaySurfaceStats = {
  runs: number;
  errors: number;
  durationsMs: number[];
  lastErrorAt: string | null;
  lastErrorReason: string | null;
};

type UiInfraDayStats = Record<UiInfraSurface, UiInfraDaySurfaceStats>;

type UiInfraBaselineStore = {
  byDay: Record<string, UiInfraDayStats>;
  updatedAt: string;
};

export type UiInfraSurfaceSummary = {
  surface: UiInfraSurface;
  runs: number;
  errors: number;
  errorRatePct: number;
  avgMs: number;
  p95Ms: number;
  slowRuns: number;
  slowRatePct: number;
  lastErrorAt: string | null;
  lastErrorReason: string | null;
  alert: boolean;
};

export type UiInfraBaselineSummary = {
  windowDays: number;
  surfaces: Record<UiInfraSurface, UiInfraSurfaceSummary>;
  totalRuns: number;
  totalErrors: number;
  alert: boolean;
};

const STORAGE_KEY = "tension_ui_infra_baseline_v1";
const MAX_DAYS = 30;
const MAX_DURATIONS_PER_DAY = 200;
const SLOW_RUN_MS = 1500;
const ERROR_RATE_ALERT_PCT = 10;
const P95_ALERT_MS = 2500;

function isoDayKey(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

function dateKeyDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return isoDayKey(d);
}

function defaultSurfaceStats(): UiInfraDaySurfaceStats {
  return {
    runs: 0,
    errors: 0,
    durationsMs: [],
    lastErrorAt: null,
    lastErrorReason: null,
  };
}

function defaultDayStats(): UiInfraDayStats {
  return {
    home_social_load: defaultSurfaceStats(),
    admin_social_dashboard_refresh: defaultSurfaceStats(),
  };
}

function defaultStore(): UiInfraBaselineStore {
  return {
    byDay: {},
    updatedAt: new Date().toISOString(),
  };
}

export function readUiInfraBaselineStore(): UiInfraBaselineStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return defaultStore();
    return {
      byDay: parsed.byDay && typeof parsed.byDay === "object" ? parsed.byDay : {},
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch {
    return defaultStore();
  }
}

function writeUiInfraBaselineStore(store: UiInfraBaselineStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // noop
  }
}

function clampDurationMs(durationMs: number): number {
  if (!Number.isFinite(durationMs)) return 0;
  return Math.max(0, Math.round(durationMs));
}

function pruneOldDays(store: UiInfraBaselineStore): void {
  const keys = Object.keys(store.byDay).sort();
  if (keys.length <= MAX_DAYS) return;
  const toDrop = keys.slice(0, keys.length - MAX_DAYS);
  toDrop.forEach((key) => delete store.byDay[key]);
}

function getOrCreateDaySurfaceStats(
  store: UiInfraBaselineStore,
  dayKey: string,
  surface: UiInfraSurface,
): UiInfraDaySurfaceStats {
  if (!store.byDay[dayKey]) store.byDay[dayKey] = defaultDayStats();
  if (!store.byDay[dayKey][surface]) store.byDay[dayKey][surface] = defaultSurfaceStats();
  return store.byDay[dayKey][surface];
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.max(0, Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[idx] || 0;
}

function buildSurfaceSummary(
  surface: UiInfraSurface,
  byDay: Record<string, UiInfraDayStats>,
  windowDays: number,
): UiInfraSurfaceSummary {
  let runs = 0;
  let errors = 0;
  const durations: number[] = [];
  let lastErrorAt: string | null = null;
  let lastErrorReason: string | null = null;

  for (let i = 0; i < windowDays; i += 1) {
    const day = byDay[dateKeyDaysAgo(i)];
    const stats = day?.[surface];
    if (!stats) continue;
    runs += Number(stats.runs || 0);
    errors += Number(stats.errors || 0);
    if (Array.isArray(stats.durationsMs)) {
      durations.push(...stats.durationsMs.map((value) => clampDurationMs(Number(value))));
    }
    if (stats.lastErrorAt && (!lastErrorAt || stats.lastErrorAt > lastErrorAt)) {
      lastErrorAt = stats.lastErrorAt;
      lastErrorReason = stats.lastErrorReason || null;
    }
  }

  const avgMs =
    durations.length > 0 ? Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length) : 0;
  const p95Ms = Math.round(percentile(durations, 95));
  const slowRuns = durations.filter((value) => value >= SLOW_RUN_MS).length;
  const errorRatePct = runs > 0 ? Math.round((errors / runs) * 100) : 0;
  const slowRatePct = runs > 0 ? Math.round((slowRuns / runs) * 100) : 0;
  const alert = errorRatePct >= ERROR_RATE_ALERT_PCT || p95Ms >= P95_ALERT_MS;

  return {
    surface,
    runs,
    errors,
    errorRatePct,
    avgMs,
    p95Ms,
    slowRuns,
    slowRatePct,
    lastErrorAt,
    lastErrorReason,
    alert,
  };
}

export function getUiInfraBaselineSummary(windowDays = 7): UiInfraBaselineSummary {
  const boundedWindow = Math.max(1, Math.min(30, Math.round(windowDays)));
  const store = readUiInfraBaselineStore();
  const home = buildSurfaceSummary("home_social_load", store.byDay, boundedWindow);
  const admin = buildSurfaceSummary("admin_social_dashboard_refresh", store.byDay, boundedWindow);

  return {
    windowDays: boundedWindow,
    surfaces: {
      home_social_load: home,
      admin_social_dashboard_refresh: admin,
    },
    totalRuns: home.runs + admin.runs,
    totalErrors: home.errors + admin.errors,
    alert: home.alert || admin.alert,
  };
}

export function recordUiInfraRun(params: {
  surface: UiInfraSurface;
  durationMs: number;
  status: UiInfraStatus;
  reason?: string | null;
}): void {
  const dayKey = isoDayKey();
  const nowIso = new Date().toISOString();
  const store = readUiInfraBaselineStore();
  const surfaceStats = getOrCreateDaySurfaceStats(store, dayKey, params.surface);

  surfaceStats.runs += 1;
  if (params.status === "error") {
    surfaceStats.errors += 1;
    surfaceStats.lastErrorAt = nowIso;
    surfaceStats.lastErrorReason =
      typeof params.reason === "string" && params.reason.trim().length > 0
        ? params.reason.slice(0, 120)
        : "unknown_error";
  }
  surfaceStats.durationsMs.push(clampDurationMs(params.durationMs));
  if (surfaceStats.durationsMs.length > MAX_DURATIONS_PER_DAY) {
    surfaceStats.durationsMs = surfaceStats.durationsMs.slice(
      surfaceStats.durationsMs.length - MAX_DURATIONS_PER_DAY,
    );
  }

  store.updatedAt = nowIso;
  pruneOldDays(store);
  writeUiInfraBaselineStore(store);

  void trackTelemetryEvent("ui_infra_run", {
    surface: params.surface,
    status: params.status,
    durationMs: clampDurationMs(params.durationMs),
    reason: params.reason || null,
  });
}
